/**
 * useInviteCode — 邀请码"防丢失"持久化
 *
 * 场景：朋友点开邀请链接（/subscribe?invite=XXXXXX）后，在官网任意页面浏览
 * （路由跳转 / 刷新 / 切换 tab），邀请码不能丢。实现：
 *   - 入口捕获：任一页面 URL 带 ?invite= → 校验格式（6 位大小写+数字）→ 写 localStorage
 *   - 全程存活：所有页面从 store 读，不依赖 URL 参数幸存（第三方登录整页跳转同源不丢码）
 *   - last-click 归因：新码覆盖旧码（点新朋友的链接即完成更换）
 *   - TTL 24 小时：超出即视为邀请关系失效，后续注册/订阅无缓存则不再绑定；用户可随时在邀请横幅清除
 *
 * 存储 key：yfb.invite = { code, capturedAt }
 */
import { ref } from 'vue'

const INVITE_STORAGE_KEY = 'yfb.invite'
const INVITE_TTL_MS = 24 * 60 * 60 * 1000 // 24 小时归因窗口
const INVITE_CODE_REGEX = /^[A-Za-z0-9]{6}$/

interface InviteStore {
  code: string
  capturedAt: number
}

function isStore(v: unknown): v is InviteStore {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return typeof o.code === 'string' && INVITE_CODE_REGEX.test(o.code) && typeof o.capturedAt === 'number'
}

function readStore(): InviteStore | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(INVITE_STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as unknown
    if (!isStore(data)) return null
    // TTL 过期 → 清除
    if (Date.now() - data.capturedAt > INVITE_TTL_MS) {
      localStorage.removeItem(INVITE_STORAGE_KEY)
      return null
    }
    return data
  } catch {
    return null
  }
}

function writeStore(code: string) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(INVITE_STORAGE_KEY, JSON.stringify({ code, capturedAt: Date.now() }))
  } catch {
    // 隐私模式 → 忽略
  }
}

/** 从 URL 查询参数捕获邀请码（供 App setup / 路由守卫调用，多处可重复调用幂等） */
export function captureInviteFromQuery(code: string | null | undefined) {
  if (!code) return
  const trimmed = String(code).trim()
  if (!INVITE_CODE_REGEX.test(trimmed)) return
  writeStore(trimmed)
}

export function useInviteCode() {
  const store = ref<InviteStore | null>(readStore())

  /** 当前生效的邀请码（可能为 null） */
  function get(): string | null {
    if (!store.value) {
      store.value = readStore()
    }
    return store.value?.code ?? null
  }

  /** 手动输入 / 设置邀请码（订阅页输入框兜底） */
  function set(code: string) {
    const trimmed = String(code).trim()
    if (!INVITE_CODE_REGEX.test(trimmed)) return false
    writeStore(trimmed)
    store.value = { code: trimmed, capturedAt: Date.now() }
    return true
  }

  /** 清除（邀请横幅「清除」入口 / 登录自邀识别调用；注册前清除即不会被绑定归因） */
  function clear() {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(INVITE_STORAGE_KEY)
      } catch {
        // ignore
      }
    }
    store.value = null
  }

  return { get, set, clear, captureFromQuery: captureInviteFromQuery }
}
